import { isEqual } from 'lodash';
import { useEffect, useRef, useState } from 'react';

import viewProviders from '@/shared/api/fulfilment/providers/viewProviders';
import useDataGridTable from '@/shared/components/dataDisplay/DataGridTable/hooks/useDataGridTable';
import useTableColumnVisibility from '@/shared/components/dataDisplay/DataGridTable/hooks/useTableColumnVisibility';
import useRedirectLogin from '@/shared/hooks/useRedirectLogin';
import Provider from '@/shared/types/fulfilment/provider.type';

const tableColumnsList = [
  'fulfilment_delay_time',
  'fulfilment_provider_id',
  'fulfilment_provider_key',
  'fulfilment_update_interval',
  'is_auto_fulfil',
  'is_force_inventory_sync',
  'provider_description',
  'provider_name',
  'ts_created',
  'ts_last_fulfilment_update',
  'ts_modified',
];

const tableRequiredColumns = [
  'fulfilment_provider_id',
  'provider_name',
  'fulfilment_provider_key',
  'provider_description',
  'actions',
];

type Args = {
  refreshTableIndex?: number;
};

const useFulfilmentProvidersTable_v2 = ({ refreshTableIndex }: Args = {}) => {
  const redirectLogin = useRedirectLogin();

  const { apiRef, model, requestBody } = useDataGridTable();
  const requestBodyRef = useRef<typeof requestBody | null>(null);

  const [table, setTable] = useState({
    data: [] as Provider[],
    totalRows: 0,
    isLoading: false,
  });

  const { columns, visibilityModel, setVisibilityModel } =
    useTableColumnVisibility({
      tableColumnsList: tableColumnsList,
      tableRequiredColumns: tableRequiredColumns,
    });

  useEffect(() => {
    const fetchProviders = async () => {
      setTable((prev) => ({ ...prev, isLoading: true }));

      const response = await viewProviders(requestBody, redirectLogin);

      setTable((prev) => ({ ...prev, isLoading: false }));

      if (response.success) {
        setTable((prev) => ({
          ...prev,
          data: response.data,
          totalRows: response.total_records || 0,
        }));
      }
    };

    if (!isEqual(requestBody, requestBodyRef.current) || refreshTableIndex) {
      requestBodyRef.current = requestBody;

      void fetchProviders();
    }
  }, [refreshTableIndex, requestBody, redirectLogin]);

  return {
    apiRef,
    model,
    table,
    columns,
    visibilityModel,
    setVisibilityModel,
  };
};

export default useFulfilmentProvidersTable_v2;
